/**
 * «Editar» y «Borrar» de la ficha de inmueble.
 *
 * Los dos botones existían desde la fase 1 marcados con `phaseTwo`: inertes y con un `title`
 * que remitía a la hoja de ruta. La escritura llegó —el panel de alta ya edita, y la API tiene
 * `PATCH` y `DELETE` desde entonces—, pero estos dos se quedaron sin conectar. Aquí se
 * conectan; no hay nada nuevo en el servidor.
 *
 * BORRAR ES LÓGICO, NO FÍSICO, y la interfaz no debe insinuar lo contrario: hay cargos, pagos
 * y movimientos que apuntan al inmueble, y borrarlo de verdad dejaría la contabilidad
 * colgando. El servidor marca `deleted_at`, arrastra las habitaciones y se niega con un 409 si
 * queda algún contrato ACTIVO — eso último se comprueba en el servidor, no aquí: esta pantalla
 * solo lo adelanta para que el usuario no descubra el problema después de escribir el nombre.
 *
 * EL «DESHACER» DEL AVISO ES LA ÚNICA PUERTA A `POST /properties/:id/restore`, y no es un
 * atajo perezoso: el listado no sabe filtrar por borrados —`propertyListQuerySchema` no tiene
 * ese filtro—, así que una papelera necesitaría antes trabajo en el servidor. Mientras tanto,
 * el momento en que alguien quiere recuperar un inmueble es justo el siguiente al borrarlo.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PropertyDetailResponse, PropertyStatus } from '@naar/shared';
import { ApiError, api, queryKeys } from '../../api/client';
import { useCan } from '../../auth/AuthContext';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { ConfirmDialog } from '../../components/overlay/Drawer';
import { Select } from '../../components/form/Field';
import { toast } from '../../components/feedback/Toaster';
import { PropertyFormDrawer } from './PropertyFormDrawer';
import styles from './PropertyActions.module.css';

const STATUS_OPTIONS = [
  { value: 'activa', label: 'Activa' },
  { value: 'en_reforma', label: 'En reforma' },
  { value: 'vendida', label: 'Vendida' },
];

export function PropertyActions({ property }: { property: PropertyDetailResponse }) {
  /**
   * Se OCULTAN los botones que la API rechazaría, con la misma tabla de permisos que aplica el
   * servidor (`@naar/shared`). Un gestor puede editar pero no borrar: enseñarle «Borrar» para
   * devolverle un 403 es lo que hace que una aplicación parezca rota funcionando bien.
   */
  const canUpdate = useCan('property:update');
  const canDelete = useCan('property:delete');

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const occupied = property.units.filter((unit) => unit.currentLease !== null);

  function invalidateLists() {
    void queryClient.invalidateQueries({ queryKey: ['properties'] });
    void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const restore = useMutation({
    mutationFn: () => api.restoreProperty(property.id),
    onSuccess: () => {
      invalidateLists();
      toast.success(`«${property.name}» recuperado.`);
      navigate(`/propiedades/${property.id}`);
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido recuperar.');
    },
  });

  const changeStatus = useMutation({
    mutationFn: (status: PropertyStatus) => api.changePropertyStatus(property.id, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.property(property.id) });
      invalidateLists();
      toast.success('Estado actualizado.');
    },
    onError: (error: unknown) => {
      /**
       * Marcar como vendida con contratos vivos devuelve 409 con el recuento dentro. Se
       * muestra tal cual y el desplegable vuelve solo al estado real, porque su valor sale de
       * `property.status` y no de un estado local que se hubiera quedado con la mentira.
       */
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido cambiar el estado.');
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteProperty(property.id),
    onSuccess: () => {
      invalidateLists();
      queryClient.removeQueries({ queryKey: queryKeys.property(property.id) });
      toast.success(`«${property.name}» borrado.`, {
        label: 'Deshacer',
        run: () => restore.mutate(),
      });
      setConfirming(false);
      /**
       * `replace`: quedarse en la ficha de algo que ya no existe da un error de carga, y con
       * el historial normal el botón «atrás» devolvería justo a esa pantalla rota.
       */
      navigate('/propiedades', { replace: true });
    },
    onError: (error: unknown) => {
      // El 409 de contratos activos trae un mensaje que ya dice qué hacer: se muestra tal cual.
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido borrar.');
      setConfirming(false);
    },
  });

  return (
    <>
      {canUpdate && (
        /**
         * El estado va en un desplegable y no en el panel de edición porque no es un dato del
         * inmueble como los metros o el año: es una decisión con reglas propias en el servidor
         * —vender con contratos vivos se rechaza— y se toma mirando la ficha, no rellenando un
         * formulario. Su valor sale siempre de `property.status`: si el servidor rechaza el
         * cambio, el desplegable vuelve solo a la verdad.
         */
        <label className={styles.status}>
          <span className={styles.statusLabel}>Estado</span>
          <Select
            options={STATUS_OPTIONS}
            value={property.status}
            disabled={changeStatus.isPending}
            onChange={(event) => {
              const next = event.target.value as PropertyStatus;
              if (next === property.status) return;
              changeStatus.mutate(next);
            }}
            aria-label="Estado del inmueble"
          />
        </label>
      )}

      {canUpdate && (
        <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
          <Icon name="edit" size={15} strokeWidth={2} />
          Editar
        </Button>
      )}
      {canDelete && (
        <Button variant="danger" size="sm" onClick={() => setConfirming(true)}>
          <Icon name="trash" size={15} strokeWidth={2} />
          Borrar
        </Button>
      )}

      <PropertyFormDrawer
        open={editing}
        onClose={() => setEditing(false)}
        property={property}
      />

      {confirming && (
        <ConfirmDialog
          title="Borrar el inmueble"
          body="Deja de aparecer en la cartera y sus habitaciones se van con él. El histórico de cargos, pagos y movimientos se conserva."
          confirmLabel="Borrar"
          tone="danger"
          /**
           * Confirmación fuerte: hay que escribir el nombre. No es teatro — es la puerta que
           * separa «he pulsado sin querer» de «quiero borrar ESTE inmueble», y la ficha se
           * abre desde una tabla donde el de al lado se parece mucho.
           */
          requireText={property.name}
          requireTextHint={
            <>
              Para confirmar, escribe el nombre del inmueble: <code>{property.name}</code>
            </>
          }
          onCancel={() => setConfirming(false)}
          onConfirm={async () => {
            await remove.mutateAsync();
          }}
        >
          <ul className={styles.impact}>
            <li>
              <span className={styles.count}>{property.units.length}</span>
              <span>
                habitación{property.units.length === 1 ? '' : 'es'} se borra
                {property.units.length === 1 ? '' : 'n'} con el inmueble
              </span>
            </li>
            {occupied.length > 0 && (
              <li>
                <span className={styles.count}>{occupied.length}</span>
                <span>
                  habitación{occupied.length === 1 ? '' : 'es'} con contrato:{' '}
                  <b>si alguno sigue activo el servidor rechazará el borrado</b> y habrá que
                  rescindirlo antes
                </span>
              </li>
            )}
          </ul>
        </ConfirmDialog>
      )}
    </>
  );
}
